import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Accordion,
  Row,
  Col,
  Spinner,
  Alert,
  Button,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { useReturnManifestDetailContext } from 'providers/ReturnManifestDetailProvider';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';
import UnsavedChangesModal from 'components/add_return/return-manifest/ReturnManifestDetail/UnsavedChangesModal';

import { returnManifestApi } from 'api';
import { extractApiErrorMessage } from 'utils/apiErrors';
import paths from 'routes/paths';
import { useUserContext } from 'providers/UserProvider';
import { generateReturnManifestPdf } from 'utils/pdfGenerators';
import { formatStatus } from 'utils/valueFormatters';
import { getReturnManifestBadgeColor } from 'constants/returnManifest';
import { TIME_OPTION } from 'constants/time';

const ReturnManifestDetailSummary = ({
  returnManifestId,
  showActionBtn = false,
  listenToUpdates = false,
}) => {
  const navigate = useNavigate();
  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const [activeKeys, setActiveKeys] = useState(['0', '1']);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const { hasUnsavedChanges, setReturnManifestStatus } =
    useReturnManifestDetailContext();

  const {
    data: returnManifestData,
    loading,
    error,
    refetch,
  } = useFetchById(returnManifestApi, returnManifestId);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        refetch();
      };

      window.addEventListener('return-manifest-updated', handleUpdate);

      return () =>
        window.removeEventListener('return-manifest-updated', handleUpdate);
    }
  }, [listenToUpdates, refetch]);

  useEffect(() => {
    if (returnManifestData) {
      setReturnManifestStatus(returnManifestData?.return_manifest_status);
    }
  }, [returnManifestData, setReturnManifestStatus]);

  const handleCloseUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleSelect = key => {
    setActiveKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleCloseReturnManifest = async () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }

    try {
      await returnManifestApi.closeReturnManifest(returnManifestId);

      toast.success('Successfully closed return manifest');
      navigate(paths.returns.list(tenant, warehouse));
    } catch (error) {
      // 429 is already toasted by the global httpClient interceptor
      if (error.response?.status !== 429) {
        toast.error(
          extractApiErrorMessage(error) || 'Failed to close return manifest',
        );
      }
    }
  };

  const handleDiscardReturnManifest = async () => {
    try {
      await returnManifestApi.discardReturnManifest(returnManifestId);

      toast.success('Successfully discarded return manifest');
      navigate(paths.returns.list(tenant, warehouse));
    } catch (error) {
      // 429 is already toasted by the global httpClient interceptor
      if (error.response?.status !== 429) {
        toast.error(
          extractApiErrorMessage(error) || 'Failed to discard return manifest',
        );
      }
    }
  };

  const handlePrintReturnManifest = async (
    returnManifestId,
    returnManifestCode,
    returnManifestCreatedDate,
  ) => {
    try {
      const params = new URLSearchParams();
      params.append('paging', 'false');

      const response = await returnManifestApi.getShipmentsOfReturnManifest(
        returnManifestId,
        params,
      );

      generateReturnManifestPdf(
        response,
        returnManifestCode,
        returnManifestCreatedDate,
      );
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  if (loading) {
    return (
      <Card className="d-flex justify-content-center align-items-center p-4">
        <Spinner animation="border" variant="primary" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {showActionBtn && (
        <div className="d-flex justify-content-end gap-2 mb-3">
          {returnManifestData?.shipment_quantity > 0 ? (
            <>
              {returnManifestData?.return_manifest_status ===
              'closed' ? null : (
                <Button
                  variant="falcon-default"
                  size="sm"
                  onClick={handleCloseReturnManifest}
                >
                  Close Return Manifest
                </Button>
              )}

              <Button
                variant="falcon-default"
                size="sm"
                onClick={() =>
                  handlePrintReturnManifest(
                    returnManifestId,
                    returnManifestData.return_manifest_code,
                    returnManifestData.created,
                  )
                }
              >
                <FontAwesomeIcon icon="file-alt" />
              </Button>
            </>
          ) : returnManifestData?.return_manifest_status ===
            'discarded' ? null : (
            <Button
              variant="falcon-default"
              size="sm"
              onClick={handleDiscardReturnManifest}
            >
              Discard Return Manifest
            </Button>
          )}
        </div>
      )}

      <Accordion activeKey={activeKeys} onSelect={handleSelect}>
        <Card>
          {/* General Information */}
          <Accordion.Item eventKey="0">
            <Accordion.Header className="d-flex justify-content-between">
              <span className="fw-semibold">General Information</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Code
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {returnManifestData?.return_manifest_code}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Status
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    <SubtleBadge
                      bg={getReturnManifestBadgeColor(
                        returnManifestData?.return_manifest_status,
                      )}
                    >
                      {formatStatus(returnManifestData?.return_manifest_status)}
                    </SubtleBadge>
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Shipping Provider
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {returnManifestData?.shipping_provider
                      ?.shipping_provider_type || '-'}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Date
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {new Date(returnManifestData?.created).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    User
                  </Col>
                  <Col xs={12} className="fw-medium fs-10 mt-0">
                    {returnManifestData?.created_by?.email || '-'}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Summary */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="fw-semibold">Summary</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Total Shipments
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {returnManifestData?.shipment_quantity}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Card>
      </Accordion>

      <UnsavedChangesModal
        show={showUnsavedChangesModal}
        onHide={handleCloseUnsavedChangesModal}
        message="Please save your changes before closing Return Manifest."
      />
    </>
  );
};

export default ReturnManifestDetailSummary;
